import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useI18n } from '@/lib/i18n'
import { GamePlayer, GameStatusData } from '@/lib/types'
import { buildPublicPlayerProfileUrl } from '@/lib/arena'
import { cn } from '@/lib/utils'
import { ROLE_EMOJIS } from '@repo/sdk'
import { ExternalLink, User } from 'lucide-react'
import {
  RoleHypothesisPicker,
  type ReplaceRoleHypothesesInput,
  type ReplaceRoleHypothesesResult,
} from './role-hypothesis-picker'
import {
  LogPerspectiveControls,
  type LogView,
} from './log-perspective-controls'

export type { LogView } from './log-perspective-controls'

interface PlayerListProps {
  className?: string
  data?: GameStatusData
  showRoles?: boolean
  onPlayerClick?: (player: GamePlayer) => void
  viewedPlayerId?: string
  enablePlayerLogSelection?: boolean
  showLogPerspectiveControls?: boolean
  logView?: LogView
  onLogViewChange?: (view: LogView) => void
  roleBoardOverride?: GameStatusData['roleBoard']
  onReplaceRoleHypotheses?: (
    input: ReplaceRoleHypothesesInput,
  ) => Promise<ReplaceRoleHypothesesResult>
}

export function PlayerList({
  className,
  data,
  showRoles = false,
  onPlayerClick,
  viewedPlayerId,
  enablePlayerLogSelection = false,
  showLogPerspectiveControls = false,
  logView = 'god',
  onLogViewChange,
  roleBoardOverride,
  onReplaceRoleHypotheses,
}: PlayerListProps) {
  const { language, t } = useI18n()
  const players = data?.players || []
  const humanPlayerId = data?.humanPlayer?.id
  const humanRole = data?.humanPlayer?.role
  const hasRoleBoardOverride = roleBoardOverride !== undefined
  const roleBoard = hasRoleBoardOverride
    ? roleBoardOverride
    : (data?.roleBoard ?? null)
  const roleBoardSeatById = new Map(
    roleBoard?.seats.map(seat => [seat.seatId, seat] as const) ?? [],
  )
  const editableRoleBoard
    = roleBoard
      && 'editable' in roleBoard
      && roleBoard.editable
      && 'viewerSeatId' in roleBoard
      && 'candidates' in roleBoard
      ? roleBoard
      : null
  const ownRoleChip = () =>
    humanRole ? (
      <div
        data-testid="you-role-chip"
        className="flex w-fit items-center rounded-md bg-primary/15 px-1.5 py-0.5 text-xs font-medium text-primary"
      >
        {t('play.youAreRole', { role: t(`game.${humanRole}`) })}
      </div>
    ) : null
  const roleBoardIdentity = (player: GamePlayer) => {
    const identity = roleBoardSeatById.get(player.id)
    const observations = identity && 'observations' in identity
      ? identity.observations ?? []
      : []
    const observationDetails = observations.length > 0
      ? (
          <div
            className="space-y-0.5"
            data-testid={`observed-role-chip-${player.position}`}
          >
            {observations.map(observation => (
              <div
                key={observation.eventSeq}
                data-testid={`role-observation-${player.position}-${observation.roundNumber}`}
              >
                {t('play.roleHypotheses.observedOnNight', {
                  night: observation.roundNumber,
                  role: t(`game.${observation.role}`),
                })}
              </div>
            ))}
          </div>
        )
      : null
    if (identity?.state === 'known') {
      return (
        <div className="space-y-1">
          <div
            className="inline-flex w-fit max-w-full items-center rounded-md bg-primary/15 px-1.5 py-0.5 font-medium text-foreground"
            data-testid={`known-role-chip-${player.position}`}
          >
            <span className="min-w-0 break-words">
              {ROLE_EMOJIS[identity.role as keyof typeof ROLE_EMOJIS]}{' '}
              {t(`game.${identity.role}`)}
            </span>
          </div>
          {observationDetails}
        </div>
      )
    }
    if (
      identity?.state === 'unknown'
      && editableRoleBoard?.viewerSeatId === player.id
      && player.id === humanPlayerId
      && humanRole
    ) {
      return ownRoleChip()
    }
    if (
      identity?.state === 'unknown'
      && editableRoleBoard
      && player.id !== editableRoleBoard.viewerSeatId
      && onReplaceRoleHypotheses
      && 'selectedRoles' in identity
      && 'revision' in identity
    ) {
      return (
        <div className="space-y-1">
          <RoleHypothesisPicker
            key={`${editableRoleBoard.viewerSeatId}:${player.id}`}
            viewerSeatId={editableRoleBoard.viewerSeatId}
            targetSeatId={player.id}
            targetPosition={player.position}
            targetName={player.name || `Player ${player.position}`}
            selectedFactions={identity.selectedFactions}
            selectedRoles={identity.selectedRoles}
            revision={identity.revision}
            candidates={editableRoleBoard.candidates}
            onReplace={onReplaceRoleHypotheses}
          />
          {observationDetails}
        </div>
      )
    }
    return observationDetails ?? t('game.any')
  }
  const canSelectPlayer = enablePlayerLogSelection && !!onPlayerClick
  const showPlayerStatuses = data?.game?.phase === 'day'
  const getAvatarColor = (player: GamePlayer) => {
    const inherited
      = showPlayerStatuses && player.seatState === 'inherited'
    const renderAsHuman
      = player.isHuman
        || (!showPlayerStatuses && player.seatState === 'inherited')
    if (player.isDead) {
      if (inherited)
        return 'bg-red-500/25 text-red-300 border-2 border-dashed border-red-500/50'
      return renderAsHuman
        ? 'bg-red-500/30 text-red-300 border-2 border-red-500/50'
        : 'bg-red-500/20 text-red-400 border border-red-500/30'
    }
    if (inherited)
      return 'bg-primary/20 text-primary border-2 border-dashed border-primary/60'
    return renderAsHuman
      ? 'bg-primary text-primary-foreground border-2 border-primary'
      : 'bg-muted text-muted-foreground border border-border'
  }

  return (
    <Card
      className={cn(
        'player-list-container overflow-visible border-border/50 bg-background/50 backdrop-blur-sm',
        className,
      )}
      data-testid="player-list"
    >
      <CardHeader className="shrink-0 space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-lg font-medium">
            <User className="size-5 text-primary" />
            {t('play.playerList')}
          </CardTitle>
          {showLogPerspectiveControls && (
            <LogPerspectiveControls
              firstPersonLabel={t('play.logView.firstPerson')}
              godLabel={t('play.logView.god')}
              label={t('play.logView.label')}
              logView={logView}
              onLogViewChange={onLogViewChange}
            />
          )}
        </div>
      </CardHeader>
      <CardContent
        className="overflow-visible"
        data-testid="player-list-content"
      >
        <div
          className="player-list-grid grid gap-3"
          data-testid="player-list-grid"
        >
          {players.map(p => (
            <div
              key={p.id}
              className={cn(
                'relative flex items-center gap-2 rounded-lg p-2 transition-colors',
                canSelectPlayer && 'cursor-pointer hover:bg-accent/50',
                canSelectPlayer && viewedPlayerId === p.id && 'bg-accent ring-1 ring-primary/40',
              )}
            >
              {canSelectPlayer && (
                <button
                  type="button"
                  className="absolute inset-0 z-10 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  aria-label={`[${p.position}] ${p.name || `Player ${p.position}`}`}
                  aria-pressed={viewedPlayerId === p.id}
                  data-testid={`player-log-seat-${p.position}`}
                  onClick={() => onPlayerClick?.(p)}
                />
              )}
              <div className="relative size-10 shrink-0">
                <Avatar
                  className={cn(
                    'relative size-10 transition-all',
                    getAvatarColor(p),
                  )}
                >
                  <AvatarFallback
                    className="bg-transparent"
                    aria-hidden="true"
                  />
                </Avatar>
              </div>
              <div className="min-w-0 flex-1 space-y-0.5">
                <div
                  className="flex min-w-0 flex-wrap items-center gap-1"
                  data-testid={`player-name-line-${p.position}`}
                >
                  <span
                    className="shrink-0 text-sm font-semibold tabular-nums text-muted-foreground"
                    data-testid={`player-seat-number-${p.position}`}
                  >
                    {p.position}
                  </span>
                  <span className="min-w-0 max-w-full break-words text-sm font-medium">
                    {p.name || `Player ${p.position}`}
                  </span>
                  {showPlayerStatuses && p.isThinking && (
                    <span
                      className="inline-block shrink-0 animate-spin"
                      data-testid={`player-thinking-${p.position}`}
                    >
                      ⏳
                    </span>
                  )}
                  {showPlayerStatuses && p.seatState === 'inherited' && (
                    <span
                      className="shrink-0 rounded-full bg-primary/15 px-1.5 py-0.5 text-[10px] font-medium leading-none text-primary"
                      data-testid={`player-ai-managed-${p.position}`}
                    >
                      {t('play.seatTakenOver')}
                    </span>
                  )}
                </div>
                {roleBoard && (
                  <div
                    data-testid={`player-identity-line-${p.position}`}
                    className="break-words text-xs text-muted-foreground"
                  >
                    {roleBoardIdentity(p)}
                  </div>
                )}
                {!roleBoard
                  && !hasRoleBoardOverride
                  && p.id === humanPlayerId
                  && humanRole
                  && ownRoleChip()}
                {!roleBoard && !hasRoleBoardOverride && showRoles && p.role && (
                  <div className="break-words text-xs text-muted-foreground">
                    {t(`game.${p.role}`) || p.role}
                  </div>
                )}
                {(p.modelDisplayName || p.modelName) && !showRoles && (
                  p.profileUsername
                    ? (
                        <div data-testid={`player-profile-line-${p.position}`}>
                          <a
                            href={buildPublicPlayerProfileUrl(p.profileUsername, language)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            data-testid={`player-profile-link-${p.position}`}
                            title={t('play.viewProfile')}
                            className="relative z-20 inline-flex max-w-full items-center gap-1 break-words text-xs text-muted-foreground underline decoration-dotted underline-offset-2 hover:text-foreground"
                          >
                            <span className="truncate">{p.modelDisplayName || p.modelName}</span>
                            <ExternalLink className="size-3 shrink-0" aria-hidden="true" />
                          </a>
                        </div>
                      )
                    : (
                        <div className="break-words text-xs text-muted-foreground">
                          {p.modelDisplayName || p.modelName}
                        </div>
                      )
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
